import { Download } from "lucide-react";
import { FadeUp, Overline, MaskedLines } from "../components/Reveal";
import Seo from "../components/Seo";
import { LINKS, PRESS_BIO_SHORT, PRESS_BIO_LONG, PRESS_ASSETS, AWARDS } from "../data/content";

const FACTS = [
  { label: "Current Role", value: "Founder & CEO, ThirsTea USA" },
  { label: "Book", value: "The Franchise Mindset (June 2026)" },
  { label: "Based In", value: "California, United States" },
  { label: "Born", value: "India — immigrated to the US in 2002" },
  { label: "Education", value: "MBA, Institute of Management Studies (1997)" },
  { label: "Community", value: "President, Asian Business Resource Group of Southern California" },
];

export default function Press() {
  return (
    <main data-testid="press-page" className="pt-40">
      <Seo
        title="Press Kit — Gurpreet Paul"
        description="Official press kit for Gurpreet Paul, Founder & CEO of ThirsTea USA: approved biographies, downloadable portraits and book cover, key facts and media contact."
      />
      <header className="max-w-[1400px] mx-auto px-6 lg:px-12 pb-20">
        <FadeUp>
          <Overline>Press & Media</Overline>
        </FadeUp>
        <MaskedLines
          lines={["Press Kit"]}
          className="font-display font-medium tracking-tighter leading-[0.95] text-6xl sm:text-7xl lg:text-8xl text-paper mt-6"
          delay={0.15}
        />
        <FadeUp delay={0.35}>
          <p className="mt-8 max-w-2xl text-white/55 text-base md:text-lg leading-relaxed font-body font-light">
            Approved biographies, portraits and the book cover — ready for editorial use.
            For interviews, speaking requests and media inquiries, contact{" "}
            <a href={`mailto:${LINKS.email}`} className="text-brand hover:underline" data-testid="press-header-email">
              {LINKS.email}
            </a>.
          </p>
        </FadeUp>
      </header>

      <section data-testid="press-bios" className="border-t border-white/10">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12 py-24 grid grid-cols-1 lg:grid-cols-12 gap-16">
          <FadeUp className="lg:col-span-5">
            <Overline>Short Bio · 50 Words</Overline>
            <p className="mt-6 font-display font-light text-2xl leading-snug text-paper" data-testid="press-bio-short">
              {PRESS_BIO_SHORT}
            </p>
          </FadeUp>
          <FadeUp delay={0.15} className="lg:col-span-7">
            <Overline>Full Biography</Overline>
            <p className="mt-6 text-white/55 text-base leading-relaxed font-body font-light" data-testid="press-bio-long">
              {PRESS_BIO_LONG}
            </p>
          </FadeUp>
        </div>
      </section>

      <section data-testid="press-facts" className="border-t border-white/10 bg-[#0D0D0F]">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12 py-24">
          <FadeUp>
            <Overline>Key Facts</Overline>
          </FadeUp>
          <div className="mt-12 grid grid-cols-1 md:grid-cols-2 gap-px bg-white/10 border border-white/10">
            {FACTS.map((fact, i) => (
              <FadeUp key={fact.label} delay={i * 0.05}>
                <div className="bg-[#0A0A0A] p-8 h-full" data-testid={`press-fact-${i + 1}`}>
                  <p className="text-[11px] uppercase tracking-[0.25em] text-brand font-body">{fact.label}</p>
                  <p className="mt-3 text-paper font-body font-light text-lg">{fact.value}</p>
                </div>
              </FadeUp>
            ))}
          </div>
        </div>
      </section>

      <section data-testid="press-assets" className="border-t border-white/10">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12 py-24">
          <FadeUp>
            <Overline>Approved Imagery</Overline>
            <h2 className="font-display font-medium tracking-tighter leading-[0.95] text-4xl sm:text-5xl text-paper mt-6">
              Download portraits & the book cover
            </h2>
          </FadeUp>
          <div className="mt-16 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {PRESS_ASSETS.map((asset, i) => (
              <FadeUp key={asset.label} delay={i * 0.06}>
                <div
                  className="group border border-white/10 bg-[#111113] overflow-hidden hover:border-brand/50 transition-colors duration-500"
                  data-testid={`press-asset-${i + 1}`}
                >
                  <div className="h-72 overflow-hidden">
                    <img
                      src={asset.src}
                      alt={asset.label}
                      loading="lazy"
                      className={asset.contain
                        ? "w-full h-full object-contain p-10 group-hover:scale-[1.04] transition-transform duration-[1.2s]"
                        : "w-full h-full object-cover object-top group-hover:scale-[1.04] transition-transform duration-[1.2s]"}
                    />
                  </div>
                  <div className="p-6 flex items-center justify-between gap-4">
                    <div>
                      <p className="text-paper font-body">{asset.label}</p>
                      {asset.note && (
                        <p className="text-white/40 text-sm font-body font-light mt-1">{asset.note}</p>
                      )}
                    </div>
                    <a
                      href={asset.src}
                      target="_blank"
                      rel="noopener noreferrer"
                      download
                      data-testid={`press-asset-download-${i + 1}`}
                      aria-label={`Download ${asset.label}`}
                      className="shrink-0 w-11 h-11 rounded-full border border-white/15 flex items-center justify-center text-paper hover:border-brand hover:text-brand transition-colors duration-300"
                    >
                      <Download size={16} />
                    </a>
                  </div>
                </div>
              </FadeUp>
            ))}
          </div>
        </div>
      </section>

      <section data-testid="press-honors" className="border-t border-white/10 bg-[#0D0D0F]">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12 py-24">
          <FadeUp>
            <Overline>Recent Honors</Overline>
          </FadeUp>
          <div className="mt-10">
            {AWARDS.map((award, i) => (
              <FadeUp key={i} delay={0.03}>
                <div
                  className="flex flex-col sm:flex-row sm:items-baseline gap-2 sm:gap-8 py-5 border-t border-white/10 first:border-t-0"
                  data-testid={`press-honor-${i + 1}`}
                >
                  <span className="text-[12px] uppercase tracking-[0.2em] text-brand font-body shrink-0 w-20">{award.year}</span>
                  <span className="font-display font-light text-xl md:text-2xl text-paper">{award.title}</span>
                </div>
              </FadeUp>
            ))}
          </div>
        </div>
      </section>

    </main>
  );
}
